import { Prefecture, PrefKey } from 'admin/usePrefectures';
import { GetUserToken, useAuth } from 'components/AuthProvider';
import { Coords, GeocodeAddressQueryVariables } from 'generated/graphql';
import { GraphQLError } from 'graphql-request/dist/types';
import { getClientSdk } from 'graphql/client';
import { useSnackbar } from 'notistack';
import {
  queryCache,
  QueryConfig,
  TypedQueryFunction,
  useQuery,
} from 'react-query';
import { FormData } from './UpsertBoxForm';

export const geocodeKey = 'geocode';

const geocodeAddress = (
  getUserToken: GetUserToken
): TypedQueryFunction<
  Coords | null,
  [typeof geocodeKey, GeocodeAddressQueryVariables | null]
> => async (key, vars) => {
  if (!vars) return null;
  const token = await getUserToken();
  const data = await getClientSdk(token).GeocodeAddress(vars);
  return data.geocodeAddress ?? null;
};

export const getGeocodeVars = (
  formData: FormData
): GeocodeAddressQueryVariables => {
  const prefectures = queryCache.getQueryData<Prefecture[]>(PrefKey);
  const { prefecture, postcode, address } = formData;

  const prefectureName = prefectures?.find((v) => v.value === prefecture.value)
    ?.name;
  const vars: GeocodeAddressQueryVariables = {
    address: [prefectureName, postcode.trim(), address.trim()].join(' ').trim(),
  };

  return vars;
};

const useGeocodeAddress = (
  vars: GeocodeAddressQueryVariables,
  options?: QueryConfig<Coords | null, GraphQLError>
) => {
  const { getUserToken } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  return useQuery<Coords | null, GraphQLError>(
    [geocodeKey, vars],
    geocodeAddress(getUserToken),
    {
      enabled: false,
      staleTime: Infinity,
      onSuccess: (data) => {
        if (!data?.lat || !data?.lng) {
          enqueueSnackbar('経緯度が見つかりません', { variant: 'warning' });
        }
      },
      ...options,
    }
  );
};

export default useGeocodeAddress;
